<script lang="ts" setup>
import { ref, watch } from 'vue'
import type { MemberTopupSubscribeProduct, SubscribePageConfig } from '@/composables/payment'
import {
  PaymentMethodSelector,
  createShowPaymentMethodSelector,
  useSubscription,
} from '@/composables/payment'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'
import { getDailyPrice, getSavePercent, shouldShowDailyPrice } from '@/composables/payment/utils/subscribeDailyPrice'
import { VIP_TYPE } from '@/constants'

defineProps<{
  config: SubscribePageConfig
}>()

// 使用订阅 composable（支持多支付方式）
const {
  subscribeProducts,
  getUnitPrice,
  onSubscribeProducts,
  isZero,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = useSubscription('store')

// 是否显示支付方式选择器
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const { reportAddToCart } = useFbqAddToCart()
watch(subscribeProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Membership', p.preferentialPrice ?? p.price ?? 0)
})

const active = ref(0)

/**
 * 获取卡片类型
 * discount: 折扣会员
 * preferential: 畅读会员有优惠价
 * normal: 畅读会员无优惠价或免费
 */
function getCardType(item: MemberTopupSubscribeProduct): 'discount' | 'preferential' | 'normal' {
  if (item.subscribeType === VIP_TYPE.DISCOUNT)
    return 'discount'

  // 畅读会员
  if (item.preferentialPrice !== undefined && item.preferentialPrice !== null && !isZero(item.preferentialPrice))
    return 'preferential'

  return 'normal'
}
</script>

<template>
  <div class="subscribe-page">
    <BasicNavbar class="color-white" />

    <!-- Header Background -->
    <div class="header-bg">
      <img src="/images/novelarc/member_subscribe_bg.webp" alt="" class="w-full">
      <div class="header-content">
        <div class="header-title">
          {{ config.texts.vipMembership }}
        </div>
        <div class="header-desc">
          {{ config.texts.membershipDesc }}
        </div>
        <div class="header-desc">
          {{ config.texts.notJoinedYet }}
        </div>
      </div>
    </div>

    <!-- Products List -->
    <div class="products-wrap">
      <div v-for="(item, index) in subscribeProducts" :key="index">
        <div
          v-if="item.isShow"
          class="product-card"
          :class="[`card-${getCardType(item)}`, { active: active === index }]"
          @click="() => { active = index; onSubscribeProducts(item); }"
        >
          <!-- Popular Tag -->
          <div v-if="item.cornerTip === 1 && item.marketingCopy" class="popular-tag">
            {{ item.marketingCopy }}
          </div>

          <!-- 折扣会员 -->
          <template v-if="item.subscribeType === VIP_TYPE.DISCOUNT">
            <!-- 周期标签 -->
            <div class="cycle-tag">
              {{ item.cycleStr }}
            </div>

            <div class="card-content">
              <div class="card-left">
                <div class="coins-text">
                  {{ item.discountCoins }} coins
                </div>
                <ul class="desc-list">
                  <li>{{ config.texts.payTemplateGetCoins?.(item.discountOnceCoins) }}</li>
                  <li>{{ config.texts.payTemplateEarnCoins?.(item.discountBonusCoins, item.discountDailyCoins) }}</li>
                </ul>
              </div>
              <div class="card-right">
                <div class="price-btn price-btn-discount">
                  ${{ item.preferentialPrice || item.price }}
                </div>
                <div class="price-original price-original-discount">
                  $ {{ item.discountOriginalPrice }}
                </div>
              </div>
            </div>
          </template>

          <!-- 畅读会员 - 有优惠价 -->
          <template v-else-if="getCardType(item) === 'preferential'">
            <div class="card-content">
              <div class="card-left">
                <div class="title-row">
                  <div class="title-text title-text-preferential">
                    {{ item.cycleStr }}
                  </div>
                  <div v-if="shouldShowDailyPrice(item, isZero)" class="save-tag">
                    SAVE {{ getSavePercent(item) }}%
                  </div>
                </div>
                <div class="desc-text desc-text-preferential">
                  {{ item.subscribeText }}
                </div>
              </div>
              <div class="card-right">
                <div class="price-btn price-btn-preferential">
                  <template v-if="shouldShowDailyPrice(item, isZero)">
                    ${{ getDailyPrice(item) }}<span class="price-unit-suffix"> / Day</span>
                  </template>
                  <template v-else>
                    ${{ item.preferentialPrice }}
                  </template>
                </div>
                <div class="price-unit price-unit-preferential">
                  $ {{ item.price }}
                </div>
              </div>
            </div>
          </template>

          <!-- 畅读会员 - 无优惠价或免费 -->
          <template v-else>
            <div class="card-content">
              <div class="card-left">
                <div class="title-text title-text-normal">
                  {{ item.cycleStr }}
                </div>
                <div class="desc-text desc-text-normal">
                  {{ item.subscribeText }}
                </div>
              </div>
              <div class="card-right">
                <div class="price-btn price-btn-normal">
                  <template v-if="isZero(item.preferentialPrice)">
                    {{ config.texts.free }}
                  </template>
                  <template v-else>
                    ${{ item.price }}
                  </template>
                </div>
                <div v-if="getUnitPrice(item)" class="price-unit price-unit-normal">
                  $ {{ getUnitPrice(item) }}
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Tips -->
      <div class="tips-section">
        <p class="tips-title">
          {{ config.texts.tips }}
        </p>
        <p class="tips-item">
          {{ config.texts.tip1 }}
        </p>
        <p class="tips-item">
          {{ config.texts.tip2 }}
        </p>
      </div>

      <!-- Payment Method Selector -->
      <div v-if="showPaymentMethodSelector" class="fixed bottom-0px left-0 right-0 flex justify-center">
        <PaymentMethodSelector
          variant="large"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.subscribe-page {
  min-height: 100vh;
  background: #fff;
}

.header-bg {
  position: relative;

  img {
    display: block;
  }

  .header-content {
    position: absolute;
    top: 44px;
    left: 0;
    right: 0;
    padding: 0 20px;
  }

  .header-title {
    font-weight: bold;
    font-size: 28px;
    background: linear-gradient(0deg, #FFD083 0%, #F8B57E 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }

  .header-desc {
    font-size: 14px;
    color: #F8BC7E;
    margin-top: 4px;
  }
}

.products-wrap {
  padding: 16px;
  margin-top: -20px;
  position: relative;
  background: #fff;
  border-radius: 16px 16px 0 0;
}

.product-card {
  position: relative;
  padding: 20px 14px 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  overflow: hidden;

  &.active {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  // 折扣会员样式
  &.card-discount {
    background: linear-gradient(270deg, #FFECDE 0%, #FFDFB8 100%);
  }

  // 畅读会员有优惠价样式
  &.card-preferential {
    background: linear-gradient(270deg, #FFE1E4 0%, #FFD9DF 100%);
  }

  // 畅读会员无优惠价或免费样式
  &.card-normal {
    background: linear-gradient(270deg, #E9EBFE 0%, #DEE2FE 100%);
  }
}

.popular-tag {
  position: absolute;
  top: 0;
  left: 0;
  height: 16px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  background: linear-gradient(90deg, #FF4754 0%, #FEA94C 100%);
  border-radius: 8px 100px 100px 0;
  font-weight: 500;
  font-size: 10px;
  color: #fff;
}

// 折扣会员周期标签（仅折扣会员使用）
.cycle-tag {
  position: absolute;
  top: 0;
  right: 0;
  width: 117px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 8px 0 8px;
  font-size: 12px;
  background: linear-gradient(270deg, #FFD1AE 0%, #FFD5A2 100%);
  color: #775217;
  opacity: 0.9;
}

.card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-left {
  flex: 1;
}

.card-right {
  flex-shrink: 0;
  text-align: right;
}

// 折扣会员文字样式
.coins-text {
  font-weight: 500;
  font-size: 18px;
  color: #775217;
}

.desc-list {
  margin-top: 4px;
  padding-left: 16px;
  font-size: 11px;
  color: #9E7739;

  li {
    list-style: disc;
    margin-top: 2px;
  }
}

// 畅读会员文字样式
.title-text {
  font-weight: 500;
  font-size: 18px;

  &.title-text-preferential {
    color: #77382C;
  }

  &.title-text-normal {
    color: #3D426E;
  }
}

.desc-text {
  margin-top: 4px;

  &.desc-text-preferential {
    font-size: 11px;
    color: #A96D61;
  }

  &.desc-text-normal {
    font-size: 14px;
    color: #797CA0;
  }
}

// 价格按钮样式
.price-btn {
  width: 81px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  font-size: 14px;
  color: #FFFFFF;

  &.price-btn-discount {
    background: linear-gradient(90deg, #F7A82A 0%, #FE7E01 100%);
  }

  &.price-btn-preferential {
    background: linear-gradient(90deg, #FF7FED 0%, #FF516B 100%);
  }

  &.price-btn-normal {
    background: linear-gradient(90deg, #C77CFF 0%, #6773F3 100%);
  }
}

// 原价/划线价样式
.price-original {
  margin-top: 4px;
  font-size: 14px;
  text-decoration-line: line-through;

  &.price-original-discount {
    color: #9E7739;
  }
}

.price-unit {
  margin-top: 4px;
  font-size: 12px;

  &.price-unit-preferential {
    color: #A96D61;
    text-decoration-line: line-through;
  }

  &.price-unit-normal {
    color: #797CA0;
  }
}

// Tips 区域
.tips-section {
  padding: 16px 4px 100px;
  font-size: 10px;
  color: #999;

  .tips-title {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .tips-item {
    margin-top: 6px;
  }
}

.title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.save-tag {
  background: rgba(247, 73, 132, 0.15);
  color: rgba(247, 73, 132, 1);
  font-size: 10px;
  font-weight: 500;
  border-radius: 4px;
  padding: 3px 6px 4px 6px;
  line-height: 8px;
}

.price-unit-suffix {
  font-size: 12px;
  font-weight: normal;
  opacity: 0.7;
}
</style>
